import { memo, useMemo } from "react";
import type {
  BangkokFloodWatch,
  CampusFloodEvent,
  CampusFloodStation,
  FallbackTier,
  RainfallStation,
  WaterGauge,
} from "@chula/shared";
import { PanelHeader } from "./PanelHeader";
import { PanelState, STALE_AFTER_MIN } from "./PanelState";

interface Props {
  watch: BangkokFloodWatch | null;
  gauges: WaterGauge[];
  rain: RainfallStation[];
  canals: WaterGauge[];
  /** Nearest FloodDash gauge stations to campus (station-level detail). */
  stations?: CampusFloodStation[];
  /** Recent FloodDash events near campus / Bangkok. */
  events?: CampusFloodEvent[];
  loading?: boolean;
  ageMinutes?: number | null;
  fallbackTier?: FallbackTier;
  onFlyTo?: (lng: number, lat: number) => void;
  /** Refetch the feed — shown as Retry when the source is offline. */
  onRetry?: () => void;
}

const STATION_LEVEL_COLOR = (level: number | null): string => {
  if (level == null) return "var(--ink-low)";
  if (level >= 5) return "var(--bad)";
  if (level >= 4) return "var(--warn)";
  if (level >= 3) return "var(--good)";
  return "var(--ink-mid)";
};

/** Short relative age for FloodDash event timestamps (ISO, UTC). */
function eventAge(iso: string): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const mins = Math.max(0, Math.round((Date.now() - t) / 60_000));
  if (mins < 60) return `${mins}m`;
  if (mins < 48 * 60) return `${Math.round(mins / 60)}h`;
  return `${Math.round(mins / (24 * 60))}d`;
}

const BAND_COLOR: Record<BangkokFloodWatch["band"], string> = {
  normal: "var(--good)",
  watch: "var(--warn)",
  elevated: "var(--neg)",
  high: "var(--bad)",
};

const BAND_LABEL: Record<BangkokFloodWatch["band"], string> = {
  normal: "NORMAL",
  watch: "WATCH",
  elevated: "ELEVATED",
  high: "CRITICAL",
};

function FactorBar({ label, value }: { label: string; value: number }) {
  return (
    <div className="flood-factor" title={`${label}: ${value}`}>
      <span className="flood-factor-lbl mono">{label}</span>
      <span className="flood-factor-bar">
        <span className="flood-factor-fill" style={{ width: `${Math.min(100, value)}%` }} />
      </span>
      <span className="flood-factor-val mono">{value}</span>
    </div>
  );
}

export const FloodWatchPanel = memo(function FloodWatchPanel({
  watch,
  gauges,
  rain,
  canals,
  stations = [],
  events = [],
  loading,
  ageMinutes,
  fallbackTier,
  onFlyTo,
  onRetry,
}: Props) {
  const nearWater = useMemo(
    () =>
      stations
        .filter((s) => s.kind === "water")
        .sort((a, b) => a.distanceKm - b.distanceKm)
        .slice(0, 4),
    [stations],
  );
  // FloodDash relays the same story from several outlets (LINE TODAY, the
  // original paper); three rows of one headline is one event, not three.
  const recentEvents = useMemo(() => uniqueByTitle(events).slice(0, 3), [events]);
  const hotGauges = useMemo(
    () =>
      [...gauges]
        .filter((g) => g.situationLevel >= 4)
        .sort((a, b) => b.situationLevel - a.situationLevel)
        .slice(0, 4),
    [gauges],
  );
  const topRain = useMemo(() => rain.slice(0, 3), [rain]);
  const nearCanals = useMemo(() => canals.slice(0, 4), [canals]);

  if (!watch) {
    const state = loading ? "loading" : fallbackTier === "unavailable" ? "error" : "empty";
    return (
      <div className="left-section flood-watch">
        <PanelHeader title="FLOOD WATCH" ageMinutes={ageMinutes} fallbackTier={fallbackTier ?? null} source="thaiwater" />
        <PanelState kind={state} what="Bangkok flood readings" source="HII ThaiWater / FloodDash" ageMinutes={ageMinutes} onRetry={onRetry} />
      </div>
    );
  }

  return (
    <div className="left-section flood-watch">
      <PanelHeader
        title="FLOOD WATCH · BKK"
        ageMinutes={ageMinutes}
        fallbackTier={fallbackTier ?? null}
        source={watch.source === "flooddash" ? "flooddash" : "thaiwater"}
        actions={
          <span className="mono caption" style={{ color: BAND_COLOR[watch.band] }}>
            {BAND_LABEL[watch.band]} · {watch.score}
          </span>
        }
      />
      {fallbackTier === "cache" && ageMinutes != null && ageMinutes >= STALE_AFTER_MIN && (
        <PanelState kind="stale" what="flood readings" ageMinutes={ageMinutes} />
      )}

      <div className="flood-score-row">
        <span className="flood-score mono" style={{ color: BAND_COLOR[watch.band] }}>{watch.score}</span>
        <div className="flood-score-meta">
          <span className="mono caption" style={{ color: BAND_COLOR[watch.band] }}>{BAND_LABEL[watch.band]}</span>
          <span className="caption" style={{ color: "var(--ink-mid)" }}>
            L4×{watch.stationsL4} · L5×{watch.stationsL5}
            {watch.maxRain24h != null ? ` · rain ${Math.round(watch.maxRain24h)} mm/24h` : ""}
          </span>
        </div>
      </div>

      <div className="flood-factors">
        <FactorBar label="WATER" value={watch.water} />
        <FactorBar label="RAIN" value={watch.rain} />
        <FactorBar label="FCST" value={watch.forecast} />
        <FactorBar label="WET" value={watch.wetness} />
        <FactorBar label="RISE" value={watch.rise} />
      </div>

      <p className="flood-action caption">{watch.actionEn}</p>
      {watch.actionTh && watch.actionTh !== watch.actionEn && (
        <p className="flood-action-th caption" lang="th">{watch.actionTh}</p>
      )}

      {nearWater.length > 0 && (
        <div className="flood-list">
          <span className="caption mono" style={{ color: "var(--ink-low)", letterSpacing: "0.08em" }}>NEAR CAMPUS · FLOODDASH</span>
          <ul>
            {nearWater.map((s) => (
              <li key={s.id}>
                <button
                  type="button"
                  className="flood-list-btn"
                  onClick={() => onFlyTo?.(s.lng, s.lat)}
                  title={`${s.name} · ${s.distanceKm} km · ${s.levelMsl != null ? `${s.levelMsl.toFixed(2)} m MSL` : "no level"}`}
                >
                  <span className="flood-list-lvl mono" style={{ color: STATION_LEVEL_COLOR(s.situationLevel) }}>
                    {s.situationLevel != null ? `L${s.situationLevel}` : "—"}
                  </span>
                  <span className="flood-list-name">{s.name}</span>
                  <span className="flood-list-trend mono">{s.distanceKm} km</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {recentEvents.length > 0 && (
        <div className="flood-list">
          <span className="caption mono" style={{ color: "var(--ink-low)", letterSpacing: "0.08em" }}>EVENTS · BKK</span>
          <ul>
            {recentEvents.map((e) => (
              <li key={e.id}>
                <button
                  type="button"
                  className="flood-list-btn"
                  onClick={() => { if (e.lng != null && e.lat != null) onFlyTo?.(e.lng, e.lat); }}
                  title={e.titleTh || e.titleEn}
                >
                  <span
                    className="flood-list-lvl mono"
                    style={{ color: e.severity >= 3 ? "var(--bad)" : e.severity >= 2 ? "var(--warn)" : "var(--info)" }}
                  >
                    {eventAge(e.at)}
                  </span>
                  <span className="flood-list-name">{e.titleEn}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {hotGauges.length > 0 && (
        <div className="flood-list">
          <span className="caption mono" style={{ color: "var(--ink-low)", letterSpacing: "0.08em" }}>HIGH WATER</span>
          <ul>
            {hotGauges.map((g) => (
              <li key={g.id}>
                <button
                  type="button"
                  className="flood-list-btn"
                  onClick={() => onFlyTo?.(g.lng, g.lat)}
                  title={`${g.name} · L${g.situationLevel}`}
                >
                  <span className="flood-list-lvl mono" style={{ color: g.situationLevel >= 5 ? "var(--bad)" : "var(--warn)" }}>
                    L{g.situationLevel}
                  </span>
                  <span className="flood-list-name">{g.name}</span>
                  <span className="flood-list-trend mono">{g.trend === "rising" ? "↑" : g.trend === "falling" ? "↓" : "·"}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {topRain.length > 0 && (
        <div className="flood-list">
          <span className="caption mono" style={{ color: "var(--ink-low)", letterSpacing: "0.08em" }}>RAIN 24H</span>
          <ul>
            {topRain.map((r) => (
              <li key={r.id}>
                <button
                  type="button"
                  className="flood-list-btn"
                  onClick={() => onFlyTo?.(r.lng, r.lat)}
                >
                  <span className="flood-list-lvl mono" style={{ color: "var(--info)" }}>
                    {r.rain24h != null ? `${Math.round(r.rain24h)}` : "—"}
                  </span>
                  <span className="flood-list-name">{r.name}</span>
                  <span className="flood-list-trend mono">mm</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {nearCanals.length > 0 && (
        <div className="flood-list">
          <span className="caption mono" style={{ color: "var(--ink-low)", letterSpacing: "0.08em" }}>NEARBY KHLONGS</span>
          <ul>
            {nearCanals.map((c) => (
              <li key={c.id}>
                <button
                  type="button"
                  className="flood-list-btn"
                  onClick={() => onFlyTo?.(c.lng, c.lat)}
                >
                  <span className="flood-list-lvl mono" style={{ color: "var(--info)" }}>
                    {c.levelMsl != null ? c.levelMsl.toFixed(1) : "—"}
                  </span>
                  <span className="flood-list-name">{c.name}</span>
                  <span className="flood-list-trend mono">m</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <span className="caption mono" style={{ color: "var(--ink-low)" }}>
        HII ThaiWater · FloodDash formula · watch indicator (not official warning)
      </span>
    </div>
  );
});

/** First occurrence of each headline. The outlet suffix (" | The Bangkok
 *  Insight - LINE TODAY") is stripped and the rest compared whole, so a relay
 *  of one story merges but two stations' templated lines never do. */
export function uniqueByTitle<T extends { titleEn: string; titleTh?: string | null }>(items: T[]): T[] {
  const seen = new Set<string>();
  return items.filter((item) => {
    const key = (item.titleTh || item.titleEn)
      .replace(/\s+[|–-]\s+.*$/, "")
      .toLowerCase()
      .replace(/\s+/g, " ")
      .trim();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}
